import type {
  MarketingRequest,
  MarketingRequestItem,
  MarketingRequestItemInput,
  MarketingRequestItemType,
  MarketingRequestPriority,
  MarketingRequestProfileRef,
  MarketingRequestStage,
  MarketingRequestStockAllocationItem,
} from '../../types/marketingSolicitacao'

export const MODULE_NAME = 'marketing_solicitacao'
export const PARAMS_MODULE_NAME = 'marketing_parametros'
export const PERM_LOADING = null
export const PAGE_SIZE = 20
export const DEFAULT_STAGE_COLOR = '#6b7280'

export const SELECT_CLASS =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const ITEM_TYPE_LABELS: Record<MarketingRequestItemType, string> = {
  brinde: 'Brinde',
  peca_grafica: 'Peça gráfica ou digital',
  evento: 'Evento',
  material_impresso: 'Material impresso',
  comunicacao: 'Comunicação',
  outros: 'Outros',
}

export const ITEM_TYPE_HINTS: Record<MarketingRequestItemType, string> = {
  brinde: 'Solicitar brinde do estoque',
  peca_grafica: 'Arte gráfica ou digital',
  evento: 'Apoio a evento',
  material_impresso: 'Impressão e material físico',
  comunicacao: 'Mensagem e canal de divulgação',
  outros: 'Pedido fora das opções acima',
}

export const ITEM_TYPE_ORDER: MarketingRequestItemType[] = [
  'brinde',
  'peca_grafica',
  'evento',
  'material_impresso',
  'comunicacao',
  'outros',
]

export const PRIORITY_LABELS: Record<MarketingRequestPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
}

export const PRIORITY_COLORS: Record<MarketingRequestPriority, string> = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

export const PRIORITY_OPTIONS: { value: MarketingRequestPriority; label: string; dotClass: string }[] = [
  { value: 'low', label: 'Baixa', dotClass: 'bg-primary-500' },
  { value: 'medium', label: 'Média', dotClass: 'bg-yellow-400' },
  { value: 'high', label: 'Alta', dotClass: 'bg-red-500' },
]

export const PRIORITY_SORT_RANK: Record<MarketingRequestPriority, number> = {
  low: 1,
  medium: 2,
  high: 3,
}

export function isMarketingRequestPriority(value: unknown): value is MarketingRequestPriority {
  return value === 'low' || value === 'medium' || value === 'high'
}

/** Exibe o ID sequencial da solicitação (ex.: #42). */
export function formatRequestNumber(requestNumber: number | null | undefined): string {
  if (requestNumber == null) return '—'
  return `#${requestNumber}`
}

export { parseRequestNumberFilter } from '../../utils/marketingRequestNumber'

/** Ex.: João Silva (Marketing). */
export function formatRequesterWithDepartment(
  requester?: MarketingRequestProfileRef | null
): string {
  const name = (requester?.name || '').trim()
  if (!name) return '—'
  const dept = (
    requester?.department?.display_name ||
    requester?.department?.name ||
    ''
  ).trim()
  return dept ? `${name} (${dept})` : name
}

export function isStockDeductibleItemType(type: MarketingRequestItemType | string): boolean {
  return type === 'brinde' || type === 'material_impresso'
}

/** Solicitação em etapa de encerramento (finalizada). */
export function isRequestClosed(
  request: Pick<MarketingRequest, 'closed_at' | 'stage_id' | 'stage'> | null | undefined,
  stages?: MarketingRequestStage[]
): boolean {
  if (!request) return false
  if (request.closed_at) return true
  if (request.stage?.is_closed) return true
  if (!stages?.length) return false
  return Boolean(stages.find((s) => s.id === request.stage_id)?.is_closed)
}

export function parseItemQuantity(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const normalized = String(raw ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : NaN
}

/** Agrupa itens brinde/material da solicitação para a tela de baixa. */
export function buildStockAllocationItems(
  items: Array<MarketingRequestItem | MarketingRequestItemInput>
): MarketingRequestStockAllocationItem[] {
  const map = new Map<string, MarketingRequestStockAllocationItem>()

  for (const item of items) {
    if (!isStockDeductibleItemType(item.item_type)) continue
    const details = (item.details || {}) as Record<string, unknown>
    const stockItemId = String(details.item_id || '').trim()
    if (!stockItemId) {
      throw new Error('Há item de brinde/material sem vínculo com o estoque.')
    }
    const qty = parseItemQuantity(details.quantidade)
    if (!Number.isFinite(qty) || qty <= 0) {
      throw new Error('Informe uma quantidade válida para todos os itens de estoque.')
    }
    const label =
      String(details.item || details.material || '').trim() || stockItemId
    const existing = map.get(stockItemId)
    if (existing) {
      existing.quantity += qty
    } else {
      map.set(stockItemId, {
        stock_item_id: stockItemId,
        label,
        quantity: qty,
        lines: [{ location_id: '', quantity: qty }],
      })
    }
  }

  return Array.from(map.values())
}

export function emptyDetailsForType(type: MarketingRequestItemType): Record<string, string> {
  switch (type) {
    case 'brinde':
      return {
        item_id: '',
        item: '',
        quantidade: '',
        destinatario: '',
        finalidade: '',
        endereco: '',
        personalizacao: '',
      }
    case 'peca_grafica':
      return {
        formato: '',
        onde_sera_veiculada: '',
        medidas: '',
        texto_base: '',
        referencias: '',
      }
    case 'evento':
      return {
        data: '',
        local: '',
        publico_estimado: '',
        o_que_marketing_fornece: '',
        responsavel_no_local: '',
      }
    case 'material_impresso':
      return {
        item_id: '',
        material: '',
        quantidade: '',
        prazo: '',
        destino_fisico: '',
      }
    case 'comunicacao':
      return {
        canal: '',
        mensagem_principal: '',
        publico: '',
        data_veiculacao: '',
      }
    case 'outros':
      return {
        descricao: '',
        justificativa: '',
      }
  }
}

export function validateItemDetails(
  type: MarketingRequestItemType,
  details: Record<string, string>
): string | null {
  const req = (key: string, label: string) => {
    if (!String(details[key] || '').trim()) return `${label} é obrigatório.`
    return null
  }

  switch (type) {
    case 'brinde':
      return req('item_id', 'Item') || req('quantidade', 'Quantidade')
    case 'peca_grafica':
      return req('formato', 'Formato') || req('onde_sera_veiculada', 'Onde será veiculada')
    case 'evento':
      return (
        req('data', 'Data') ||
        req('local', 'Local') ||
        req('o_que_marketing_fornece', 'O que o marketing fornece')
      )
    case 'material_impresso':
      return (
        req('item_id', 'Material') ||
        req('quantidade', 'Quantidade') ||
        req('prazo', 'Prazo') ||
        req('destino_fisico', 'Destino físico')
      )
    case 'comunicacao':
      return req('canal', 'Canal') || req('mensagem_principal', 'Mensagem principal')
    case 'outros':
      return req('descricao', 'Descrição') || req('justificativa', 'Justificativa')
  }
}

export function summarizeItems(items: MarketingRequestItem[] | undefined): string {
  if (!items?.length) return 'Sem itens'
  const counts = new Map<MarketingRequestItemType, number>()
  for (const item of items) {
    counts.set(item.item_type, (counts.get(item.item_type) || 0) + 1)
  }
  return ITEM_TYPE_ORDER.filter((type) => counts.has(type))
    .map((type) => {
      const n = counts.get(type) || 0
      const label = ITEM_TYPE_LABELS[type].toLowerCase()
      return `${n} ${n === 1 ? singularLabel(type) : pluralLabel(type, label)}`
    })
    .join(', ')
}

function singularLabel(type: MarketingRequestItemType): string {
  if (type === 'brinde') return 'brinde'
  if (type === 'peca_grafica') return 'peça gráfica'
  if (type === 'evento') return 'evento'
  if (type === 'material_impresso') return 'material impresso'
  if (type === 'comunicacao') return 'comunicação'
  return 'outro'
}

function pluralLabel(type: MarketingRequestItemType, fallback: string): string {
  if (type === 'brinde') return 'brindes'
  if (type === 'peca_grafica') return 'peças gráficas'
  if (type === 'evento') return 'eventos'
  if (type === 'material_impresso') return 'materiais impressos'
  if (type === 'comunicacao') return 'comunicações'
  if (type === 'outros') return 'outros'
  return fallback
}

export function formatDateOnlyPtBr(value: string | null | undefined): string {
  if (!value) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return '—'
  return `${match[3]}/${match[2]}/${match[1]}`
}

export function formatDateTimePtBr(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function slugifyStageKey(name: string, taken: Set<string>): string {
  let base = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
  if (!base) base = 'etapa'
  let candidate = base
  let n = 2
  while (taken.has(candidate)) {
    candidate = `${base}_${n}`
    n += 1
  }
  return candidate
}

export function tabButtonClass(active: boolean): string {
  return `py-3 px-1 border-b-2 font-medium text-sm ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`
}

export function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}
